/**
 * Iter53 P34 — Extras din SaleOverview.jsx (self-contained).
 *
 * Model de validitate a vânzării pe 3 axe (QC PPC / SF / Extern):
 *   - `computeValidity(sale, contract, contractStatus)` → { qc, sf, ext, isInvalid, reasons }
 *   - `StatusValidityCard` — card colorat per axă (positive / negative / neutral)
 *
 * NU cuprinde acțiunile inline (dropdown QC, buton Marchează renunțare) —
 * acelea rămân în SaleOverview pentru că depind de state părinte (draft, saving).
 */
import { Shield, ShieldOff, ShieldX } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

export const SF_STATUSES = [
  { value: "signed", label: "Signed (semnat)" },
  { value: "activated", label: "Activated (activat)" },
  { value: "terminated", label: "Terminated (anulat)" },
];

/**
 * Model de validitate — QC PPC (contract_status.quality_call_outcome),
 * SF (sale.salesforce_contract_state — col V din raportul SF, autoritativ) și
 * Extern (sale.is_withdrawn — binar, marcaj manual).
 */
export const computeValidity = (sale, c, cs) => {
  const qcRaw = (cs?.quality_call_outcome || sale?.quality_call_outcome || "").trim();
  const qcLower = qcRaw.toLowerCase();
  const sfRaw = (sale?.salesforce_contract_state || cs?.salesforce_contract_state || "").trim();
  const sfLower = sfRaw.toLowerCase();
  const isWithdrawn = !!sale?.is_withdrawn;

  const qcNegative = /\bnu confirm|refuzat|respins|cancel|anulat/.test(qcLower);
  const qcPositive = !qcNegative && /\bconfirm|ok|valid|acceptat/.test(qcLower);

  const sfNegative = sfLower === "terminated" || sfLower === "anulat" || sfLower === "ko";
  const sfPositive = sfLower === "activat" || sfLower === "activated" || sfLower === "signed" || sfLower === "semnat";

  return {
    qc: {
      state: qcNegative ? "negative" : qcPositive ? "positive" : "neutral",
      label: qcRaw || "Neevaluat",
    },
    sf: {
      state: sfNegative ? "negative" : sfPositive ? "positive" : "neutral",
      label: sfRaw || "Nedefinit",
    },
    // Marcaj manual binar. Lipsa renunțării NU confirmă furnizarea activă → neutral, nu positive.
    ext: {
      state: isWithdrawn ? "negative" : "neutral",
      label: isWithdrawn ? "Renunțare marcată" : "Fără renunțare marcată",
      badge: isWithdrawn ? "RENUNȚARE" : "NEMARCAT",
    },
    isInvalid: qcNegative || sfNegative || isWithdrawn,
    reasons: [
      qcNegative && `Quality Call: ${qcRaw}`,
      sfNegative && `Status SF: ${sfRaw}`,
      isWithdrawn && (sale?.withdrawn_reason ? `Renunțare: ${sale.withdrawn_reason}` : "Marcată ca renunțare"),
    ].filter(Boolean),
  };
};

const stateTheme = {
  positive: {
    card: "bg-emerald-50 border-emerald-200",
    icon: "text-emerald-700 bg-emerald-100",
    title: "text-emerald-900",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    IconCmp: Shield,
  },
  negative: {
    card: "bg-red-50 border-red-300",
    icon: "text-red-700 bg-red-100",
    title: "text-red-900",
    badge: "bg-red-100 text-red-800 border-red-300",
    IconCmp: ShieldX,
  },
  neutral: {
    card: "bg-slate-50 border-slate-200",
    icon: "text-slate-600 bg-slate-100",
    title: "text-slate-900",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    IconCmp: ShieldOff,
  },
};

export const StatusValidityCard = ({ title, label, state, helper, testId, action, badgeLabel, titleHint }) => {
  const t = stateTheme[state] || stateTheme.neutral;
  const Icon = t.IconCmp;
  return (
    <Card className={`border ${t.card} shadow-sm`} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center ${t.icon}`}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <p className={`text-[11px] uppercase tracking-wider font-semibold ${t.title}`} title={titleHint}>{title}</p>
          </div>
          <Badge className={`${t.badge} border text-[10px] px-2`} data-testid={`${testId}-state`}>
            {badgeLabel || (state === "positive" ? "OK" : state === "negative" ? "INVALID" : "PENDING")}
          </Badge>
        </div>
        <p className={`mt-2 text-sm font-semibold ${t.title}`} data-testid={`${testId}-label`}>{label}</p>
        {helper && <p className="mt-0.5 text-[11px] text-slate-500">{helper}</p>}
        {action && <div className="mt-2">{action}</div>}
      </CardContent>
    </Card>
  );
};

export default StatusValidityCard;
